import React from "react";

import XIcon from "@uui/XClose";

import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import { LABEL_FOR_ENTITY_TYPE } from "@app/analytics/TopicReport/utils";
import type { TopicEntity } from "@app/analytics/types";

import AutoAnimateContainer from "@rui/AutoAnimateContainer";
import IconButton, { IconButtonProps } from "@rui/IconButton";
import { Link } from "@rui/Link";
import Stack, { Row } from "@rui/Stack";

const InnerContainer = styled(Stack)(({ theme }) => ({
  borderRadius: "8px",
  border: "1px solid",
  borderColor: theme.palette.shades.neutral[100],
  backgroundColor: "#FFF",
  boxShadow: "0px 8px 24px 0px #0000001A",
  padding: theme.spacing(1.5),
  color: theme.palette.shades.neutral[800],
}));

const OuterContainer = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2, 2, 1),
}));

const Label = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilySecondary,
  fontWeight: 500,
}));

const Text = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilySecondary,
  lineHeight: 19 / 16,
}));

const CloseButton = styled<React.FC<IconButtonProps>>((props) => (
  <IconButton {...props}>
    <XIcon />
  </IconButton>
))(({ theme }) => ({
  height: "28px",
  width: "28px",
  border: "1px solid",
  borderColor: theme.palette.shades.neutral[100],
  transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  "& .MuiSvgIcon-root": {
    transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    fontSize: "20px",
    color: theme.palette.shades.neutral[400],
  },
  "&:hover": {
    borderColor: theme.palette.shades.primary[500],
    "& .MuiSvgIcon-root": {
      color: theme.palette.shades.primary[500],
    },
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  fontSize: "0.75rem",
  color: theme.palette.shades.secondary[400],
  fontFamily: theme.typography.fontFamilySecondary,
}));

interface HighlightedEntityProps {
  entity?: TopicEntity;
  onClickView: (entity: TopicEntity) => void;
  onDismiss: () => void;
}

const HighlightedEntity = ({
  entity,
  onClickView,
  onDismiss,
}: HighlightedEntityProps) => {
  // it shouldn't be possible for a title entity to be highlighted, but return null here just in case
  if (!entity || entity.kind === "title") return null;

  const handleViewInTimeline = () => onClickView(entity);

  return (
    <AutoAnimateContainer show={Boolean(entity)}>
      <OuterContainer>
        <InnerContainer spacing={1}>
          <Row variant="apart">
            <Label>{LABEL_FOR_ENTITY_TYPE[entity.kind]}</Label>
            <CloseButton onClick={onDismiss} />
          </Row>
          <Text>{entity.text}</Text>
          <StyledLink onClick={handleViewInTimeline}>
            View in timeline
          </StyledLink>
        </InnerContainer>
      </OuterContainer>
    </AutoAnimateContainer>
  );
};

const HighlightedEntityWrapper = (props: HighlightedEntityProps) => (
  <AutoAnimateContainer show={Boolean(props.entity)}>
    <HighlightedEntity {...props} />
  </AutoAnimateContainer>
);

export default HighlightedEntityWrapper;
